import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Button, Progress, message, Modal } from 'antd';
import { 
	LockOutlined, 
	SafetyCertificateOutlined, 
	ClockCircleOutlined,
	WifiOutlined,
	DeleteOutlined
} from '@ant-design/icons';
import PatternLock from '../pattern-lock/PatternLock';
import PasscodeLock from '../passcode-lock/PasscodeLock';
import { useSecurity } from '../../hooks/useSecurity';
import { useI18n } from '../../hooks/useI18n';
import './SecurityLock.scss';

const { Title, Text } = Typography;

const SecurityLock = () => {
	const { 
		isLocked,
		lockType,
		unlockWithPattern,
		unlockWithPasscode,
		removeLockAndLogout,
		isPatternType,
		isPasscodeType 
	} = useSecurity();
	const { t } = useI18n();
	const [attemptCount, setAttemptCount] = useState(0);
	const [isBlocked, setIsBlocked] = useState(false);
	const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
	const [currentTime, setCurrentTime] = useState(new Date());

	const MAX_ATTEMPTS = 5;
	const BLOCK_DURATION = 30; // seconds

	// Update current time every second
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentTime(new Date());
		}, 1000);

		return () => clearInterval(timer);
	}, []);

	// Handle blocking after max attempts
	useEffect(() => {
		if (attemptCount >= MAX_ATTEMPTS && !isBlocked) {
			setIsBlocked(true);
			setBlockTimeRemaining(BLOCK_DURATION);
			message.error(`Too many failed attempts. Blocked for ${BLOCK_DURATION} seconds.`);
			
			const blockTimer = setInterval(() => {
				setBlockTimeRemaining((prev) => {
					if (prev <= 1) {
						setIsBlocked(false);
						setAttemptCount(0);
						clearInterval(blockTimer);
						message.success('You can try again now.');
						return 0;
					}
					return prev - 1;
				});
			}, 1000);

			return () => clearInterval(blockTimer);
		}
	}, [attemptCount, isBlocked]);

	const handlePatternComplete = (pattern) => {
		if (isBlocked) return;

		const success = unlockWithPattern(pattern);
		if (success) {
			setAttemptCount(0);
			message.success(t('security.unlock_success'));
		} else {
			const newAttemptCount = attemptCount + 1;
			setAttemptCount(newAttemptCount);
			const remainingAttempts = MAX_ATTEMPTS - newAttemptCount;
			
			if (remainingAttempts > 0) {
				message.error(`${t('security.invalid_pattern')}. ${remainingAttempts} attempts remaining.`);
			}
		}
	};

	const handlePasscodeComplete = (passcode) => {
		if (isBlocked) return;

		const success = unlockWithPasscode(passcode);
		if (success) {
			setAttemptCount(0);
			message.success(t('security.unlock_success'));
		} else {
			const newAttemptCount = attemptCount + 1;
			setAttemptCount(newAttemptCount);
			const remainingAttempts = MAX_ATTEMPTS - newAttemptCount;
			
			if (remainingAttempts > 0) {
				message.error(`${t('security.invalid_passcode')}. ${remainingAttempts} attempts remaining.`);
			}
		}
	};

	const handleRemoveLock = () => {
		Modal.confirm({
			title: t('security.remove_lock_and_logout'),
			content: t('security.remove_lock_logout_confirm'),
			okType: 'danger',
			icon: <DeleteOutlined />,
			zIndex: 10000, // Higher than security-lock-overlay z-index (9999)
			onOk: () => {
				removeLockAndLogout();
			}
		});
	};

	if (!isLocked) {
		return null;
	}

	return (
		<>
			{isBlocked ? (
				<div className="security-lock-overlay">
					<div className="security-lock-container">
						<Card className="block-screen">
							<Space direction="vertical" size="large" style={{ textAlign: 'center', width: '100%' }}>
								<SafetyCertificateOutlined 
									style={{ 
										fontSize: '80px', 
										color: '#ff4d4f',
										animation: 'pulse 2s infinite'
									}} 
								/>
								<Title level={2} style={{ color: '#ff4d4f', margin: 0 }}>
									{t('security.app_locked')}
								</Title>
								<Text type="secondary" style={{ fontSize: '16px' }}>
									{t('security.too_many_attempts')}
								</Text>
								<div style={{ width: '100%', maxWidth: '300px' }}>
									<Progress
										type="circle"
										percent={((BLOCK_DURATION - blockTimeRemaining) / BLOCK_DURATION) * 100}
										format={() => `${blockTimeRemaining}s`}
										strokeColor="#ff4d4f"
										size={120}
									/>
								</div>
								<Text strong style={{ fontSize: '14px' }}>
									{t('security.try_again_in')} {blockTimeRemaining} {t('security.seconds')}
								</Text>
							</Space>
						</Card>
					</div>
				</div>
			) : (
				<div className="security-lock-overlay">
					<div className="security-lock-container">
						{/* Status Bar */}
						<div className="status-bar">
							<div className="status-item">
								<WifiOutlined style={{ color: 'white' }} />
							</div>
							<div className="status-time">
								{currentTime.toLocaleTimeString([], { 
									hour: 'numeric', 
									minute: '2-digit',
									second: '2-digit',
									hour12: true 
								})}
							</div>
							<div className="status-item">
								<LockOutlined style={{ color: 'white' }} />
							</div>
						</div>

						{/* Lock Screen Content */}
						<div className="lock-screen-content">
							{/* Header Info */}
							<div className="lock-header">
								<Space direction="vertical" size="small" style={{ textAlign: 'center' }}>
									<Title level={4} style={{ color: 'white', margin: 0 }}>
										{currentTime.toLocaleDateString(undefined, { 
											weekday: 'long',
											year: 'numeric',
											month: 'long',
											day: 'numeric'
										})}
									</Title>
									<Title level={1} style={{ color: 'white', margin: 0, fontSize: '48px' }}>
										{currentTime.toLocaleTimeString([], { 
											hour: 'numeric', 
											minute: '2-digit',
											second: '2-digit',
											hour12: true 
										})}
									</Title>
								</Space>
							</div>

							{/* Attempt Counter */}
							{attemptCount > 0 && (
								<div className="attempt-warning">
									<Card size="small" style={{ background: 'rgba(255, 77, 79, 0.1)', border: '1px solid rgba(255, 77, 79, 0.3)' }}>
										<Space>
											<ClockCircleOutlined style={{ color: '#ff4d4f' }} />
											<Text style={{ color: '#ff4d4f' }}>
												{attemptCount} failed attempt{attemptCount > 1 ? 's' : ''}. 
												{' '}{MAX_ATTEMPTS - attemptCount} remaining.
											</Text>
										</Space>
									</Card>
								</div>
							)}

							{/* Lock Interface */}
							<div className="lock-interface">
								{isPatternType && (
									<PatternLock 
										onPatternComplete={handlePatternComplete}
										title={t('security.unlock_with_pattern')}
										subtitle={t('security.draw_pattern_to_unlock')}
									/>
								)}
								
								{isPasscodeType && (
									<PasscodeLock 
										onPasscodeComplete={handlePasscodeComplete}
										title="Enter Passcode"
										subtitle="Enter your passcode to unlock the app"
									/>
								)}
							</div>

							{/* Remove Lock Option */}
							<div className="lock-actions">
								<Button 
									danger
									icon={<DeleteOutlined />}
									onClick={handleRemoveLock}
									size="small"
									style={{
										backgroundColor: 'rgba(255, 77, 79, 0.1)',
										borderColor: 'rgba(255, 77, 79, 0.3)',
										color: '#ff4d4f'
									}}
								>
									{t('security.remove_lock_and_logout')}
								</Button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default SecurityLock;